import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";
import { activityActionLabel } from "@/lib/activity-actions";
import type { ActivityAnalytics, AdminUserSummary } from "@/lib/api";
import { formatDay, formatNumber } from "@/lib/format";
import { lastDaysLabel } from "@/lib/usage-window";
import { cn } from "@/lib/utils";

import { ActivityChart } from "./activity-chart";
import { ActivityExplorer } from "./activity-explorer";

interface Props {
  analytics: ActivityAnalytics;
  /** While the figures of another period load: those on screen dim. */
  updating?: boolean;
  users: AdminUserSummary[];
  actions: string[];
}

/**
 * The Logboek: its key figures, the activity over time and per action, over
 * the period the log's time range names (the past month by default), then the
 * log itself, whose time range drives that period.
 */
export function ActivityDashboard({ analytics, users, actions, updating = false }: Props) {
  const { t } = useTranslation();
  const { summary, timeseries, breakdown } = analytics;
  const first = timeseries.at(0);
  const last = timeseries.at(-1);

  // No "Signups" tile: the dashboard has no sign-up any more. Older
  // `signup` entries still show under "By action" and in the activity log.
  const stats = [
    { key: "events", label: t("activity.events"), value: summary.total_events },
    { key: "logins", label: t("activity.logins"), value: summary.total_logins },
    { key: "users", label: t("usage.activeUsers"), value: summary.active_users },
  ];

  return (
    <div className="space-y-8">
      <div className={cn("space-y-8 transition-opacity", updating && "opacity-60")}>
        <section>
          {/* On a phone the cards come closer and a long label hyphenates, so three still fit. */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {stats.map((stat) => (
              <Card key={stat.key}>
                <CardContent className="px-3 py-3 sm:px-6 sm:py-4">
                  <p className="text-muted text-xs hyphens-auto">{stat.label}</p>
                  <p className="text-primary text-xl font-bold sm:text-2xl">
                    {formatNumber(stat.value)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-muted mt-2 text-xs">{lastDaysLabel(summary.days)}</p>
        </section>

        <section>
          <h2 className="font-heading text-primary mb-3 text-sm font-bold">
            {t("activity.overTime")}
          </h2>
          <Card>
            <CardContent className="py-6 max-sm:px-4">
              <div className="text-foreground mb-4 flex items-center gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1.5">
                  <span className="bg-chart-1 inline-block h-2.5 w-2.5" /> {t("activity.logins")}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="bg-chart-2 inline-block h-2.5 w-2.5" /> {t("activity.allEvents")}
                </span>
              </div>
              <ActivityChart data={timeseries} />
              <div className="text-muted border-border flex items-center justify-between border-t-2 pt-2 text-xs">
                <span>{first && formatDay(first.day)}</span>
                <span>{last && formatDay(last.day)}</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {breakdown.length > 0 && (
          <section>
            <h2 className="font-heading text-primary mb-3 text-sm font-bold">
              {t("activity.byAction")}
            </h2>
            <Card>
              <CardContent className="space-y-2 py-4">
                {breakdown.map((item) => (
                  <div key={item.action} className="flex items-center justify-between text-sm">
                    <span className="font-medium" title={item.action}>
                      {activityActionLabel(item.action)}
                    </span>
                    <span className="text-muted">{formatNumber(item.count)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>
        )}
      </div>

      <section>
        <h2 className="font-heading text-primary mb-3 text-sm font-bold">{t("activity.recent")}</h2>
        <ActivityExplorer users={users} actions={actions} />
      </section>
    </div>
  );
}
